import { hasSystemAdminAccess, staffHasPermission } from '@/shared/system-admin'
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTenantBrandingStore } from '@/stores/tenant-branding'
import { STAFF_PERMISSIONS } from '@/shared/permissions'
import { hasAdminPanelAccess } from '@/shared/admin-panel-access'
import { syncDocumentTitle } from '@/utils/document-title'
import { isTenantEInvoiceSetupComplete } from '@/utils/einvoice-compliance-policy'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresAuth: false, titleKey: 'auth.signInTitle' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      meta: { requiresAuth: false, titleKey: 'auth.forgotPassword.title' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
      meta: { requiresAuth: false, titleKey: 'auth.resetPassword.title' },
    },
    {
      path: '/access-denied',
      name: 'access-denied',
      component: () => import('@/views/AccessDeniedView.vue'),
      meta: { requiresAuth: false, titleKey: 'accessDenied.title' },
    },
    {
      path: '/owner-impersonation',
      name: 'owner-impersonation',
      component: () => import('@/views/auth/OwnerImpersonationView.vue'),
      meta: { requiresAuth: false, titleKey: 'ownerImpersonation.title' },
    },
    {
      path: '/catalog/seating/:planId/editor',
      name: 'catalog-seating-plan-editor',
      component: () => import('@/views/seating/SeatingPlanLayoutEditorView.vue'),
      meta: {
        requiresAuth: true,
        titleKey: 'seating.layoutEditorFullscreenButton',
        permission: STAFF_PERMISSIONS.catalogWrite,
        requiresSeatingPro: true,
      },
    },
    {
      path: '/catalog/seating/:planId/editor-beta',
      name: 'catalog-seating-plan-editor-beta',
      component: () => import('@/views/seating/SeatingPlanLayoutEditorBetaView.vue'),
      meta: {
        requiresAuth: true,
        titleKey: 'seating.layoutEditorBetaFullscreenButton',
        permission: STAFF_PERMISSIONS.catalogWrite,
        requiresSeatingPro: true,
      },
    },
    {
      path: '/products/hard-tickets/layouts/:layoutId/editor',
      name: 'hard-ticket-layout-editor',
      component: () => import('@/views/hard-tickets/HardTicketLayoutEditorView.vue'),
      meta: {
        requiresAuth: true,
        titleKey: 'hardTickets.layoutEditorTitle',
        permissionsAny: [
          STAFF_PERMISSIONS.settingsWrite,
          STAFF_PERMISSIONS.eventsWrite,
          STAFF_PERMISSIONS.catalogWrite,
        ],
        requiresHardTickets: true,
        ownerOnlyVendorScope: true,
      },
    },
    {
      path: '/',
      component: () => import('@/components/layout/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: '/dashboard' },
        {
          path: 'setup',
          name: 'setup-gate',
          component: () => import('@/views/SetupGateView.vue'),
          meta: {
            titleKey: 'dashboard.readiness.title',
            permission: STAFF_PERMISSIONS.settingsWrite,
            ownerOnlyVendorScope: true,
          },
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { titleKey: 'dashboard.title' },
        },
        {
          path: 'analytics',
          name: 'analytics',
          component: () => import('@/views/analytics/AnalyticsView.vue'),
          meta: {
            titleKey: 'analytics.title',
            permission: STAFF_PERMISSIONS.analyticsRead,
            requiresAnalytics: true,
            breadcrumbs: [{ labelKey: 'analytics.title' }],
          },
        },
        {
          path: 'marketing',
          name: 'marketing',
          component: () => import('@/views/marketing/MarketingView.vue'),
          meta: {
            titleKey: 'marketing.title',
            permission: STAFF_PERMISSIONS.marketingRead,
            requiresMarketingSuite: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'marketing.title' }],
          },
        },
        {
          path: 'marketing/campaigns/:id',
          name: 'marketing-campaign-detail',
          component: () => import('@/views/marketing/MarketingCampaignDetailView.vue'),
          meta: {
            titleKey: 'marketing.editor.title',
            permission: STAFF_PERMISSIONS.marketingRead,
            requiresMarketingSuite: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'marketing.tabs.campaigns', path: '/marketing?tab=campaigns' },
              { labelKey: 'marketing.editor.title' },
            ],
          },
        },
        {
          path: 'marketing/web-app-campaigns/:id',
          name: 'marketing-onsite-campaign-detail',
          component: () => import('@/views/marketing/MarketingOnsiteCampaignDetailView.vue'),
          meta: {
            titleKey: 'marketing.onsite.editorTitle',
            permission: STAFF_PERMISSIONS.marketingRead,
            requiresMarketingSuite: true,
            shellVariant: 'wide',
            breadcrumbs: [
              { labelKey: 'marketing.tabs.webAppCampaigns', path: '/marketing?tab=webAppCampaigns' },
              { labelKey: 'marketing.onsite.editorTitle' },
            ],
          },
        },
        {
          path: 'marketing/automations/:id',
          name: 'marketing-automation-detail',
          component: () => import('@/views/marketing/MarketingAutomationDetailView.vue'),
          meta: {
            titleKey: 'marketing.automations.editorTitle',
            permission: STAFF_PERMISSIONS.marketingRead,
            requiresMarketingSuite: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'marketing.tabs.automations', path: '/marketing?tab=automations' },
              { labelKey: 'marketing.automations.editorTitle' },
            ],
          },
        },
        {
          path: 'marketing/contacts/:id',
          name: 'marketing-contact-detail',
          component: () => import('@/views/marketing/MarketingContactDetailView.vue'),
          meta: {
            titleKey: 'marketing.contacts.detailTitle',
            permission: STAFF_PERMISSIONS.marketingRead,
            requiresMarketingSuite: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'marketing.tabs.contacts', path: '/marketing?tab=contacts' },
              { labelKey: 'marketing.contacts.detailTitle' },
            ],
          },
        },
        {
          path: 'pos',
          name: 'pos',
          component: () => import('@/views/pos/PosSellView.vue'),
          meta: {
            titleKey: 'pos.title',
            permissions: [STAFF_PERMISSIONS.ordersWrite, STAFF_PERMISSIONS.eventsRead],
            breadcrumbs: [{ labelKey: 'pos.title' }],
          },
        },
        {
          path: 'orders',
          name: 'orders',
          component: () => import('@/views/orders/OrdersListView.vue'),
          meta: {
            titleKey: 'orders.title',
            permission: STAFF_PERMISSIONS.ordersRead,
            breadcrumbs: [{ labelKey: 'orders.title' }],
          },
        },
        {
          path: 'orders/:id',
          name: 'order-detail',
          component: () => import('@/views/orders/OrderDetailView.vue'),
          meta: {
            titleKey: 'orders.detailTitle',
            permission: STAFF_PERMISSIONS.ordersRead,
            breadcrumbs: [
              { labelKey: 'orders.title', path: '/orders' },
              { labelKey: 'orders.detailTitle' },
            ],
          },
        },
        {
          path: 'finance/payment-attention',
          name: 'payment-attention',
          component: () => import('@/views/finance/PaymentAttentionView.vue'),
          meta: {
            titleKey: 'paymentAttention.title',
            permission: STAFF_PERMISSIONS.paymentsRead,
            breadcrumbs: [{ labelKey: 'paymentAttention.title' }],
          },
        },
        {
          path: 'hard-tickets',
          redirect: (to) => ({ path: '/products/hard-tickets', query: to.query }),
        },
        {
          path: 'products/hard-tickets',
          name: 'hard-tickets',
          component: () => import('@/views/hard-tickets/HardTicketsView.vue'),
          meta: {
            titleKey: 'hardTickets.title',
            permissionsAny: [
              STAFF_PERMISSIONS.settingsWrite,
              STAFF_PERMISSIONS.eventsWrite,
              STAFF_PERMISSIONS.catalogWrite,
            ],
            requiresHardTickets: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'hardTickets.title' },
            ],
          },
        },
        {
          path: 'finance/vouchers',
          name: 'vouchers',
          component: () => import('@/views/finance/VouchersListView.vue'),
          meta: {
            titleKey: 'vouchers.title',
            permission: STAFF_PERMISSIONS.financeRead,
            breadcrumbs: [{ labelKey: 'vouchers.title' }],
          },
        },
        {
          path: 'finance/e-invoices',
          name: 'finance-e-invoices',
          component: () => import('@/views/finance/EInvoiceArtifactsView.vue'),
          meta: {
            titleKey: 'eInvoices.title',
            permission: STAFF_PERMISSIONS.financeRead,
            ownerOnlyVendorScope: true,
            requiresEInvoiceCompliance: true,
            breadcrumbs: [{ labelKey: 'eInvoices.title' }],
          },
        },
        {
          path: 'finance/vouchers/:id',
          name: 'voucher-detail',
          component: () => import('@/views/finance/VoucherDetailView.vue'),
          meta: {
            titleKey: 'vouchers.detailTitle',
            permission: STAFF_PERMISSIONS.financeRead,
            breadcrumbs: [
              { labelKey: 'vouchers.title', path: '/finance/vouchers' },
              { labelKey: 'vouchers.detailTitle' },
            ],
          },
        },
        {
          path: 'finance/gift-card-products',
          name: 'gift-card-products',
          component: () => import('@/views/finance/GiftCardProductsListView.vue'),
          meta: {
            titleKey: 'giftCardProducts.title',
            permission: STAFF_PERMISSIONS.financeRead,
            requiresGiftCards: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'giftCardProducts.title' }],
          },
        },
        {
          path: 'finance/gift-card-products/:id',
          name: 'gift-card-product-detail',
          component: () => import('@/views/finance/GiftCardProductDetailView.vue'),
          meta: {
            titleKey: 'giftCardProducts.detailTitle',
            permission: STAFF_PERMISSIONS.financeRead,
            requiresGiftCards: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'giftCardProducts.title', path: '/finance/gift-card-products' },
              { labelKey: 'giftCardProducts.detailTitle' },
            ],
          },
        },
        {
          path: 'reports/dashboard',
          redirect: '/dashboard',
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/views/users/StaffUsersView.vue'),
          meta: {
            titleKey: 'users.title',
            permissionsAny: [STAFF_PERMISSIONS.staffUsersRead, STAFF_PERMISSIONS.customersRead],
            breadcrumbs: [{ labelKey: 'users.title' }],
          },
        },
        {
          path: 'users/guests/:email',
          name: 'guest-detail',
          component: () => import('@/views/users/GuestDetailView.vue'),
          meta: {
            titleKey: 'users.guestDetailTitle',
            permission: STAFF_PERMISSIONS.customersRead,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'users.tabs.guests', path: '/users?tab=guests' },
              { labelKey: 'users.guestDetailTitle' },
            ],
          },
        },
        {
          path: 'users/customers/:id',
          name: 'customer-detail',
          component: () => import('@/views/users/CustomerDetailView.vue'),
          meta: {
            titleKey: 'users.customerDetailTitle',
            permission: STAFF_PERMISSIONS.customersRead,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'users.tabs.customers', path: '/users?tab=customers' },
              { labelKey: 'users.customerDetailTitle' },
            ],
          },
        },
        {
          path: 'users/:id',
          name: 'staff-user-detail',
          component: () => import('@/views/users/StaffUserDetailView.vue'),
          meta: {
            titleKey: 'users.detailTitle',
            permission: STAFF_PERMISSIONS.staffUsersRead,
            breadcrumbs: [
              { labelKey: 'users.tabs.staff', path: '/users?tab=staff' },
              { labelKey: 'users.detailTitle' },
            ],
          },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/views/profile/StaffProfileView.vue'),
          meta: { titleKey: 'profile.title', breadcrumbs: [{ labelKey: 'profile.title' }] },
        },
        {
          path: 'vendors',
          redirect: { name: 'vendors' },
        },
        {
          path: 'vendors/management',
          name: 'vendors',
          component: () => import('@/views/vendors/VendorsView.vue'),
          meta: {
            titleKey: 'vendorsPage.title',
            permission: STAFF_PERMISSIONS.settingsWrite,
            requiresVendorSystem: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'vendorsPage.title' },
            ],
          },
        },
        {
          path: 'vendors/reporting',
          name: 'vendor-reporting',
          component: () => import('@/views/vendors/VendorReportingView.vue'),
          meta: {
            titleKey: 'nav.vendorReporting',
            permission: STAFF_PERMISSIONS.settingsWrite,
            requiresVendorSystem: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'nav.vendorReporting' },
            ],
          },
        },
        {
          path: 'vendors/reporting/:vendorId',
          name: 'vendor-reporting-detail',
          component: () => import('@/views/vendors/VendorReportingDetailView.vue'),
          meta: {
            titleKey: 'nav.vendorReporting',
            permission: STAFF_PERMISSIONS.settingsWrite,
            requiresVendorSystem: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'nav.vendorReporting', path: '/vendors/reporting' },
              { labelKey: 'vendorsPage.detail.fallbackTitle' },
            ],
          },
        },
        {
          path: 'vendors/:id',
          name: 'vendor-detail',
          component: () => import('@/views/vendors/VendorDetailView.vue'),
          meta: {
            titleKey: 'vendorsPage.detail.fallbackTitle',
            permission: STAFF_PERMISSIONS.settingsWrite,
            requiresVendorSystem: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'vendorsPage.title', path: '/vendors/management' },
              { labelKey: 'vendorsPage.detail.fallbackTitle' },
            ],
          },
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/settings/SettingsView.vue'),
          meta: {
            titleKey: 'settings.title',
            permissionsAny: [STAFF_PERMISSIONS.settingsWrite, STAFF_PERMISSIONS.financeRead],
          },
          beforeEnter: (to) => {
            if (useAuthStore().user?.vendorScope?.mode === 'restricted') return true
            const legacyWebshopTabs: Record<string, string> = {
              general: 'maintenance',
              payments: 'checkout',
              branding: 'appearance',
            }
            const raw = to.query.tab
            const tab = Array.isArray(raw) ? raw[0] : raw
            if (typeof tab !== 'string') return true
            const nextTab = legacyWebshopTabs[tab]
            if (!nextTab) return true
            return {
              name: 'webshop',
              query: { tab: nextTab },
              hash: to.hash,
              replace: true,
            }
          },
        },
        {
          path: 'webshop',
          name: 'webshop',
          component: () => import('@/views/webshop/WebshopSettingsView.vue'),
          meta: {
            titleKey: 'webshop.pageTitle',
            permission: STAFF_PERMISSIONS.settingsWrite,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'webshop.pageTitle' }],
          },
          beforeEnter: (to) => {
            const legacyLegalTabs = new Set([
              'imprint',
              'privacy',
              'terms',
              'withdrawal',
              'cookies',
              'shipping',
              'accessibility',
            ])
            const raw = to.query.tab
            const tab = Array.isArray(raw) ? raw[0] : raw
            if (typeof tab === 'string' && legacyLegalTabs.has(tab)) {
              return {
                name: 'webshop',
                query: { ...to.query, tab: 'legal', legalTab: tab },
                hash: to.hash,
                replace: true,
              }
            }
            return true
          },
        },
        {
          path: 'system/diagnostics/runtime',
          name: 'system-runtime-diagnostics',
          component: () => import('@/views/system/SystemDiagnosticsView.vue'),
          props: { section: 'runtime' },
          meta: { systemAdminOnly: true, titleKey: 'systemAdmin.runtimeDiagnostics', breadcrumbs: [{ labelKey: 'systemAdmin.runtimeDiagnostics' }] },
        },
        {
          path: 'system/diagnostics/wallet',
          name: 'system-wallet-diagnostics',
          component: () => import('@/views/system/SystemDiagnosticsView.vue'),
          props: { section: 'wallet' },
          meta: { systemAdminOnly: true, titleKey: 'settings.walletStatusTitle', breadcrumbs: [{ labelKey: 'settings.walletStatusTitle' }] },
        },
        {
          path: 'system/test-data/demo',
          name: 'system-demo-data',
          component: () => import('@/views/system/SystemTestDataView.vue'),
          props: { section: 'demo' },
          meta: { systemAdminOnly: true, titleKey: 'webshop.hubTabs.demoSeeding', breadcrumbs: [{ labelKey: 'webshop.hubTabs.demoSeeding' }] },
        },
        {
          path: 'system/test-data/cleanup',
          name: 'system-sandbox-cleanup',
          component: () => import('@/views/system/SystemTestDataView.vue'),
          props: { section: 'cleanup' },
          meta: { systemAdminOnly: true, titleKey: 'webshop.hubTabs.sandboxCleanup', breadcrumbs: [{ labelKey: 'webshop.hubTabs.sandboxCleanup' }] },
        },
        {
          path: 'automations',
          name: 'automations',
          component: () => import('@/views/automations/AutomationsView.vue'),
          meta: {
            titleKey: 'automations.title',
            permission: STAFF_PERMISSIONS.automationsManage,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'automations.title' }],
          },
        },
        {
          path: 'support/feedback',
          name: 'customer-feedback',
          component: () => import('@/views/support/CustomerFeedbackListView.vue'),
          meta: {
            titleKey: 'customerFeedback.inboxTitle',
            permission: STAFF_PERMISSIONS.supportRead,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'customerFeedback.inboxTitle' }],
          },
        },
        {
          path: 'support/cases',
          name: 'support-cases',
          component: () => import('@/views/support/SupportCasesListView.vue'),
          meta: {
            titleKey: 'support.casesTitle',
            permission: STAFF_PERMISSIONS.supportRead,
            breadcrumbs: [{ labelKey: 'support.casesTitle' }],
          },
        },
        {
          path: 'support/bug-reports',
          name: 'bug-reports',
          component: () => import('@/views/support/BugReportsListView.vue'),
          meta: {
            titleKey: 'bugReports.title',
            breadcrumbs: [{ labelKey: 'bugReports.title' }],
          },
        },
        {
          path: 'support/bug-reports/:reference',
          name: 'bug-report-detail',
          component: () => import('@/views/support/BugReportDetailView.vue'),
          meta: {
            titleKey: 'bugReports.detailTitle',
            breadcrumbs: [
              { labelKey: 'bugReports.title', path: '/support/bug-reports' },
              { labelKey: 'bugReports.detailTitle' },
            ],
          },
        },
        {
          path: 'support/cases/:id',
          name: 'support-case-detail',
          component: () => import('@/views/support/SupportCaseDetailView.vue'),
          meta: {
            titleKey: 'support.caseDetailTitle',
            permission: STAFF_PERMISSIONS.supportRead,
            breadcrumbs: [
              { labelKey: 'support.casesTitle', path: '/support/cases' },
              { labelKey: 'support.caseDetailTitle' },
            ],
          },
        },
        {
          path: 'events',
          name: 'events',
          component: () => import('@/views/events/EventsListView.vue'),
          meta: {
            titleKey: 'events.title',
            permission: STAFF_PERMISSIONS.eventsRead,
            breadcrumbs: [{ labelKey: 'events.title' }],
          },
        },
        {
          path: 'events/import',
          name: 'event-import',
          component: () => import('@/views/events/EventImportView.vue'),
          meta: {
            titleKey: 'events.importTitle',
            permission: STAFF_PERMISSIONS.eventsWrite,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.importTitle' },
            ],
          },
        },
        {
          path: 'events/new',
          name: 'event-new',
          component: () => import('@/views/events/EventFormView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsWrite,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.new' },
            ],
          },
        },
        {
          path: 'events/:id/edit',
          name: 'event-edit',
          component: () => import('@/views/events/EventFormView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsWrite,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.edit' },
            ],
          },
        },
        {
          path: 'events/:eventId/dates/new',
          name: 'event-date-new',
          redirect: (to) => ({
            name: 'event-detail',
            params: { id: String(to.params.eventId) },
            query: { addDate: '1' },
          }),
        },
        {
          path: 'events/:eventId/dates/:dateId',
          name: 'event-date-detail',
          component: () => import('@/views/events/EventDateDetailView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsRead,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.dateDetail' },
            ],
          },
        },
        {
          path: 'events/:id',
          name: 'event-detail',
          component: () => import('@/views/events/EventDetailView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsRead,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.detailPageTitle' },
            ],
          },
        },
        {
          path: 'news',
          name: 'news',
          component: () => import('@/views/news/NewsListView.vue'),
          meta: {
            titleKey: 'nav.news',
            permission: STAFF_PERMISSIONS.newsRead,
            breadcrumbs: [{ labelKey: 'nav.news' }],
          },
        },
        {
          path: 'news/:id',
          name: 'news-detail',
          component: () => import('@/views/news/NewsDetailView.vue'),
          meta: {
            titleKey: 'news.detailTitle',
            permission: STAFF_PERMISSIONS.newsRead,
            breadcrumbs: [
              { labelKey: 'nav.news', path: '/news' },
              { labelKey: 'news.detailTitle' },
            ],
          },
        },
        {
          path: 'catalog/media',
          name: 'catalog-media',
          component: () => import('@/views/catalog/MediaLibraryView.vue'),
          meta: {
            titleKey: 'media.title',
            permission: STAFF_PERMISSIONS.mediaRead,
            breadcrumbs: [
              { labelKey: 'media.title' },
            ],
          },
        },
        {
          path: 'catalog/import',
          name: 'catalog-import',
          component: () => import('@/views/catalog/CatalogImportView.vue'),
          meta: {
            titleKey: 'catalog.importTitle',
            permission: STAFF_PERMISSIONS.catalogWrite,
            breadcrumbs: [
              { labelKey: 'catalog.importTitle' },
            ],
          },
        },
        {
          path: 'catalog/locations',
          name: 'catalog-locations',
          component: () => import('@/views/catalog/CatalogLocationsView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'catalog.locationsTitle' },
            ],
          },
        },
        {
          path: 'catalog/genres',
          name: 'catalog-genres',
          component: () => import('@/views/catalog/CatalogGenresView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'catalog.genresTitle' },
            ],
          },
        },
        {
          path: 'catalog/artists',
          name: 'catalog-artists',
          component: () => import('@/views/catalog/CatalogArtistsView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'catalog.artistsTitle' },
            ],
          },
        },
        {
          path: 'catalog/locations/:id',
          name: 'catalog-location-detail',
          component: () => import('@/views/catalog/CatalogLocationDetailView.vue'),
          meta: {
            titleKey: 'catalog.locationDetailTitle',
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'catalog.locationsTitle', path: '/catalog/locations' },
              { labelKey: 'catalog.locationDetailTitle' },
            ],
          },
        },
        {
          path: 'catalog/genres/:id',
          name: 'catalog-genre-detail',
          component: () => import('@/views/catalog/CatalogGenreDetailView.vue'),
          meta: {
            titleKey: 'catalog.genreDetailTitle',
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'catalog.genresTitle', path: '/catalog/genres' },
              { labelKey: 'catalog.genreDetailTitle' },
            ],
          },
        },
        {
          path: 'catalog/artists/:id',
          name: 'catalog-artist-detail',
          component: () => import('@/views/catalog/CatalogArtistDetailView.vue'),
          meta: {
            titleKey: 'catalog.artistDetailTitle',
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'catalog.artistsTitle', path: '/catalog/artists' },
              { labelKey: 'catalog.artistDetailTitle' },
            ],
          },
        },
        {
          path: 'catalog/price-categories',
          name: 'catalog-price-categories',
          component: () => import('@/views/catalog/CatalogPriceCategoriesView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsWrite,
            breadcrumbs: [
              { labelKey: 'catalog.priceCategoriesTitle' },
            ],
          },
        },
        {
          path: 'catalog/seating',
          name: 'catalog-seating',
          component: () => import('@/views/seating/SeatingPlansView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            requiresSeatingPro: true,
            breadcrumbs: [
              { labelKey: 'seating.plansTitle' },
            ],
          },
        },
        {
          path: 'catalog/seating/:planId',
          name: 'catalog-seating-plan',
          component: () => import('@/views/seating/SeatingPlanDetailView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            requiresSeatingPro: true,
            breadcrumbs: [
              { labelKey: 'seating.plansTitle', path: '/catalog/seating' },
              { labelKey: 'seating.planDetail' },
            ],
          },
        },
        {
          path: ':pathMatch(.*)*',
          name: 'not-found',
          component: () => import('@/views/NotFoundView.vue'),
          meta: { titleKey: 'notFound.title' },
        },
      ],
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (to.name === 'login' && auth.isAuthenticated) {
    if (!hasAdminPanelAccess(auth.user)) {
      auth.clear()
      return true
    }
    return { name: 'dashboard' }
  }

  if (to.meta.requiresAuth) {
    if (!auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }
    if (!hasAdminPanelAccess(auth.user)) {
      auth.clear()
      return { name: 'login' }
    }
  }

  if (to.meta.systemAdminOnly && !hasSystemAdminAccess(auth.user)) return { name: 'access-denied' }

  const requiredFromMeta = (to.meta.permissions as string[] | undefined) ?? []
  const singlePerm = to.meta.permission as string | undefined
  const required =
    requiredFromMeta.length > 0 ? requiredFromMeta : singlePerm ? [singlePerm] : []

  if (required.length > 0 && auth.isAuthenticated) {
    const ok = required.every((p) => staffHasPermission(auth.user, p))
    if (!ok) {
      return { name: 'access-denied', query: { permission: required.join(',') } }
    }
  }

  const requiredAny = (to.meta.permissionsAny as string[] | undefined) ?? []
  if (requiredAny.length > 0 && auth.isAuthenticated) {
    const ok = requiredAny.some((p) => staffHasPermission(auth.user, p))
    if (!ok) {
      return { name: 'access-denied', query: { permission: requiredAny.join(',') } }
    }
  }

  const ownerOnlyVendorScope = to.meta.ownerOnlyVendorScope === true && auth.isAuthenticated
  if (ownerOnlyVendorScope && auth.user?.vendorScope?.mode === 'restricted') {
    return {
      name: 'access-denied',
      query: { reason: 'owner_scope', redirect: to.fullPath },
    }
  }

  const tenantBrandingStore = useTenantBrandingStore()
  const needsSeatingPro = to.meta.requiresSeatingPro === true && auth.isAuthenticated
  const needsGiftCards = to.meta.requiresGiftCards === true && auth.isAuthenticated
  const needsVendorSystem = to.meta.requiresVendorSystem === true && auth.isAuthenticated
  const needsAnalytics = to.meta.requiresAnalytics === true && auth.isAuthenticated
  const needsHardTickets = to.meta.requiresHardTickets === true && auth.isAuthenticated
  const needsMarketingSuite = to.meta.requiresMarketingSuite === true && auth.isAuthenticated
  const needsEInvoiceCompliance = to.meta.requiresEInvoiceCompliance === true && auth.isAuthenticated
  if (
    needsSeatingPro ||
    needsGiftCards ||
    needsVendorSystem ||
    needsAnalytics ||
    needsHardTickets ||
    needsMarketingSuite ||
    needsEInvoiceCompliance
  ) {
    await tenantBrandingStore.load()
  }
  if (needsSeatingPro && !tenantBrandingStore.seatingProActive) {
    return {
      name: 'access-denied',
      query: { reason: 'seating_pro', redirect: to.fullPath },
    }
  }
  if (needsGiftCards && !tenantBrandingStore.giftCardsActive) {
    return {
      name: 'access-denied',
      query: { reason: 'gift_cards', redirect: to.fullPath },
    }
  }
  if (needsVendorSystem && !tenantBrandingStore.vendorSystemActive) {
    return {
      name: 'access-denied',
      query: { reason: 'vendor_system', redirect: to.fullPath },
    }
  }
  if (needsAnalytics && !tenantBrandingStore.analyticsActive) {
    return {
      name: 'access-denied',
      query: { reason: 'analytics', redirect: to.fullPath },
    }
  }
  if (needsHardTickets && !tenantBrandingStore.hardTicketsActive) {
    return {
      name: 'access-denied',
      query: { reason: 'hard_tickets', redirect: to.fullPath },
    }
  }
  if (needsMarketingSuite && !tenantBrandingStore.marketingSuiteActive) {
    return {
      name: 'access-denied',
      query: { reason: 'marketing_suite', redirect: to.fullPath },
    }
  }
  if (needsEInvoiceCompliance && !isTenantEInvoiceSetupComplete(tenantBrandingStore.eInvoicePolicy)) {
    return {
      name: 'access-denied',
      query: { reason: 'e_invoice_compliance' },
    }
  }

  return true
})

router.afterEach((to) => {
  syncDocumentTitle(to)
})

export default router
